import { useState, KeyboardEvent, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { UserMenu } from './UserMenu';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserRole } from '@/hooks/useUserRole';

const adminNavItems = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'My Policies', href: '/policies?view=mine' },
  { label: 'All Policies', href: '/policies' },
  { label: 'Policy Builder', href: '/composer' },
  { label: 'Audit Exports', href: '/audit-export' },
];

const staffNavItems = [
  { label: 'Dashboard', href: '/staff-dashboard' },
  { label: 'My Policies', href: '/policies?view=mine' },
  { label: 'All Policies', href: '/policies' },
];

export function AppHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data: userRole } = useUserRole();
  const isStaff = userRole === 'staff';
  const navItems = isStaff ? staffNavItems : adminNavItems;
  const logoHref = isStaff ? '/staff-dashboard' : '/dashboard';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  function handleSearch() {
    if (searchQuery.trim()) {
      navigate(`/policies?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }

  const isDashboard = location.pathname === '/';
  const showNav = !isDashboard;

  function isActive(href: string): boolean {
    if (href === '/') {
      return location.pathname === '/';
    }
    if (href.includes('?')) {
      const [path, query] = href.split('?');
      return location.pathname === path && location.search.includes(query.split('=')[1]);
    }
    return location.pathname === href && !location.search.includes('view=mine');
  }

  return (
    <header className="header-gradient sticky top-0 z-50">
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Left section: Logo + Nav */}
        <div className="flex items-center">
          {/* Mobile menu button */}
          {showNav && isMobile && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:text-white/80 transition-colors mr-4"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          )}

          {/* Logo */}
          <Link to={logoHref} className="flex items-center gap-2">
            <span className="logo-text text-[28px] text-white tracking-tight">
              Veracord<span style={{ fontSize: '9px', fontWeight: 400, position: 'relative', top: '-8px', marginLeft: '1px', letterSpacing: 0, fontFamily: 'Inter, sans-serif' }}>™</span>
            </span>
          </Link>

          {/* Desktop Navigation - with generous spacing from logo */}
          {showNav && !isMobile && (
            <nav className="flex items-center gap-1 ml-14">
              {navItems.map((item) => {
                const isComposerInert = item.href.startsWith('/composer') && location.pathname === '/composer';
                const classes = cn(
                  'px-3 py-2 rounded-md text-sm lg:text-sm md:text-xs font-medium transition-colors',
                  isActive(item.href)
                    ? 'bg-white/20 text-white'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                );
                if (isComposerInert) {
                  return <span key={item.href} className={cn(classes, 'cursor-default')}>{item.label}</span>;
                }
                return <Link key={item.href} to={item.href} className={classes}>{item.label}</Link>;
              })}
            </nav>
          )}
        </div>

        {/* Right section: Search + User Menu */}
        <div className="flex items-center gap-3">
          {/* Search */}
          {!isMobile ? (
            <div className="relative">
              <button
                onClick={handleSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </button>
              <Input
                type="text"
                placeholder="Search policies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-48 pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 [&::-webkit-search-cancel-button]:hidden"
              />
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-white hover:text-white/80 transition-colors p-2"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
          )}

          {/* User Menu */}
          <UserMenu />
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {showNav && isMobile && mobileMenuOpen && (
        <nav className="border-t border-white/10 bg-black/20 backdrop-blur-sm">
          <div className="container px-4 py-2">
            {navItems.map((item) => {
              const isComposerInert = item.href.startsWith('/composer') && location.pathname === '/composer';
              const classes = cn(
                'block px-3 py-3 rounded-md text-sm font-medium transition-colors',
                isActive(item.href)
                  ? 'bg-white/20 text-white'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              );
              if (isComposerInert) {
                return <span key={item.href} className={cn(classes, 'cursor-default')} onClick={() => setMobileMenuOpen(false)}>{item.label}</span>;
              }
              return <Link key={item.href} to={item.href} onClick={() => setMobileMenuOpen(false)} className={classes}>{item.label}</Link>;
            })}
          </div>
        </nav>
      )}

      {/* Mobile Search Dropdown */}
      {isMobile && searchOpen && (
        <div className="border-t border-white/10 bg-black/20 backdrop-blur-sm p-4">
          <div className="relative">
            <button
              onClick={handleSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <Input
              type="text"
              placeholder="Search policies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/60 [&::-webkit-search-cancel-button]:hidden"
              autoFocus
            />
          </div>
        </div>
      )}
    </header>
  );
}
